import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  useTheme,
  Badge,
  Stack,
  Button,
  Container,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Tooltip
} from '@mui/material';
import { 
  ShowChart,
  TrendingUp,
  TrendingDown,
  Warning,
  Assessment,
  Timeline,
  BarChart,
  MonetizationOn,
  ArrowUpward,
  ArrowDownward,
  CompareArrows,
  Analytics,
  HelpOutline,
  WatchLater,
  CalendarMonth,
  EventNote,
  EmojiObjects,
  Insights,
  Help,
  QuestionAnswer,
  NavigateNext,
  NavigateBefore,
  Dashboard,
  Refresh,
  PieChart,
  DragHandle, // Icon for market caps
  TrendingUpRounded
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { MarketReportFull, InvestmentProfile } from '../../types/investment';
import { useLocation, useNavigate } from 'react-router-dom';
import MarketDashboard from './MarketDashboard';
import MarketQuestionsView from './MarketQuestionsView';
import MarketSectorsView from './MarketSectorsView';
import MarketRecommendationsView from './MarketRecommendationsView';
import MarketRecommendations from './MarketRecommendations';
import MarketAllocationView from './MarketAllocationView';
import MarketInsightsReport from './MarketInsightsReport';
import MarketCapsView from './MarketCapsView'; // Import the MarketCapsView component
import { renderMoneyFlowRating, renderPerformanceRating, renderRiskRating } from './MarketInsightsReport';
import { getSignalColor } from '../../theme/themesV3';
import MarketFactorsView from './MarketFactorsView';

// Styled components
const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTab-root': {
    minWidth: 120,
    fontWeight: 600,
    fontSize: '0.95rem',
    textTransform: 'none',
  }
}));

const ViewContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  marginBottom: theme.spacing(4),
}));

// Interface for tab panel content
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`market-tabpanel-${index}`}
      aria-labelledby={`market-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

// Helper function for accessibility
const a11yProps = (index: number) => {
  return {
    id: `market-tab-${index}`,
    'aria-controls': `market-tabpanel-${index}`,
  };
};

// Market Analysis Workflow Component
interface MarketAnalysisWorkflowProps {
  profileId: string;
  profile: InvestmentProfile;
  onTickerSelect: (ticker: string) => void;
  marketReportFull: MarketReportFull | null;
  isLoading: boolean;
  error: string | null;
}

const MarketAnalysisWorkflow: React.FC<MarketAnalysisWorkflowProps> = ({
  profileId,
  profile,
  onTickerSelect,
  marketReportFull,
  isLoading,
  error,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Get view and subView from URL params
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentView = searchParams.get('view') || 'dashboard'; // Default to dashboard
  // Example: Get sub-view for trends if needed
  const currentSubView = searchParams.get('subView'); // e.g., 'risk', 'performance'

  // --- Navigation Handler ---
  const handleViewChange = useCallback((view: string, subView?: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', view);
    if (subView) {
      params.set('subView', subView);
    } else {
      params.delete('subView');
    }
    navigate(`${location.pathname}?${params.toString()}`);
  }, [navigate, location.pathname, searchParams]);

  // --- Rating Renderers --- (Keep these or import them)
  const renderMarketRating = useCallback((rating: string) => {
    let icon = null;
    let label = rating;
    let color = '';
    
    rating = rating?.toUpperCase() || '';
    
    switch (rating) {
      case 'BULLISH':
        icon = <TrendingUp />;
        //color = theme.palette.success.main;
        color = getSignalColor(rating);
        break;
      case 'MODERATELY BULLISH':
        icon = <TrendingUp />;
        //color = theme.palette.success.light;
        color = getSignalColor(rating);
        break;
      case 'NEUTRAL':
        icon = <CompareArrows />;
        //color = theme.palette.warning.main;
        color = getSignalColor(rating);
        break;
      case 'MODERATELY BEARISH':
        icon = <TrendingDown />;
        //color = theme.palette.error.light;
        color = getSignalColor(rating);
        break;
      case 'BEARISH':
        icon = <TrendingDown />;
        //color = theme.palette.error.main;
        color = getSignalColor(rating);
        break;
      default:
        icon = <Help />;
        //color = theme.palette.grey[500];
        color = getSignalColor(rating);
    }
  
    return (
      <Chip 
        icon={icon} 
        label={label} 
        sx={{ 
          bgcolor: color, 
          color: color === theme.palette.warning.main ? 'black' : 'white',
          fontWeight: 'bold',
          '& .MuiChip-icon': {
            color: color === theme.palette.warning.main ? 'black' : 'white',
          }
        }} 
      />
    );
  }, [theme]);

  const renderMarketTrend = useCallback((trend: string) => {
    let icon = null;
    let label = trend;
    let color = '';
    
    trend = trend?.toUpperCase() || '';
    
    switch (trend) {
      case 'IMPROVING':
        icon = <ArrowUpward />;
        //color = theme.palette.success.main;
        color = getSignalColor(trend);
        break;
      case 'STEADY':
        icon = <CompareArrows />;
        //color = theme.palette.warning.main;
        color = getSignalColor(trend);
        break;
      case 'DETERIORATING':
        icon = <ArrowDownward />;
        //color = theme.palette.error.main;
        color = getSignalColor(trend);
        break;
      default:
        icon = <Help />;
        //color = theme.palette.grey[500];
        color = getSignalColor(trend);
    }
  
    return (
      <Chip 
        icon={icon} 
        label={label} 
        sx={{ 
          bgcolor: color, 
          color: 'white',
          fontWeight: 'bold',
          '& .MuiChip-icon': {
            color: 'white',
          }
        }} 
      />
    );
  }, [theme]);

  const renderMarketRegime = useCallback((regime: string) => {
    if (!regime) {
      return (
        <Chip 
          icon={<HelpOutline />} 
          label="Unknown"
          sx={{ 
            bgcolor: theme.palette.grey[500], 
            color: 'white',
            fontWeight: 'bold',
            '& .MuiChip-icon': {
              color: 'white',
            }
          }} 
        />
      );
    }
    
    let icon = null;
    let color = '';

    if (regime.includes('Bull Market (Early/Strong Phase)')) {
      icon = <ArrowUpward />;
      //color = theme.palette.success.main;
      color = getSignalColor(regime);
    } else if (regime.includes('Bull Market (Mature/Late Phase)')) {
      icon = <TrendingUp />;
      //color = theme.palette.error.main;
      color = getSignalColor(regime);
    } else if (regime.includes('Market Top/Transition')) {
      icon = <CompareArrows />;
      //color = theme.palette.warning.main;
      color = getSignalColor(regime);
    } else if (regime.includes('Range Bound/Consolidation'))  {
      icon = <CompareArrows />;
      //color = theme.palette.error.main;
      color = getSignalColor(regime);
    } else if (regime.includes('Market Bottom Recovery')) {
      icon = <CompareArrows />;
      //color = theme.palette.error.main;
      color = getSignalColor(regime);
    } else if (regime.includes('Bear Market (Early Phase)')) {
      icon = <TrendingDown />;
      //color = theme.palette.info.main;
      color = getSignalColor(regime);
    } else if (regime.includes('Bear Market (Mature/Deep Phase)')) {
      icon = <ArrowDownward />;
      //color = theme.palette.error.main;
      color = getSignalColor(regime);
    } else {
      icon = <CompareArrows />;
      //color = theme.palette.info.main;
      color = getSignalColor(regime);
    }

    return (
      <Chip 
        icon={icon} 
        label={regime}
        sx={{ 
          bgcolor: color, 
          color: 'white',
          fontWeight: 'bold',
          '& .MuiChip-icon': {
            color: 'white',
          }
        }} 
      />
    );
  }, [theme]);

  // Map for trend-specific renderers (ensure these functions accept theme)
  const trendRatingRenderers = useMemo(() => ({
    "Money Flow": renderMoneyFlowRating,
    "Performance": renderPerformanceRating,
    "Risk": renderRiskRating,
  }), []); // Assuming these renderers are stable or imported

  // Helper function for tab navigation
  const handleTabChange = useCallback((index: number, subIndex?: number, view?: string) => {
    if (view) {
      handleViewChange(view);
    } else {
      // Map index to view
      if (index === 0) handleViewChange('dashboard');
      else if (index === 1) handleViewChange('questions');
      else if (index === 2) {
        // Trends view with subIndex
        if (subIndex === 1) handleViewChange('trends', 'risk');
        else if (subIndex === 2) handleViewChange('trends', 'money-flow');
        else handleViewChange('trends', 'performance');
      }
      else if (index === 3) handleViewChange('sectors');
      else if (index === 4) handleViewChange('market-caps');
      else if (index === 5) handleViewChange('allocation');
      else if (index === 6) handleViewChange('recommendations');
      else if (index === 7) handleViewChange('insights');
      else handleViewChange('dashboard'); // Default fallback
    }
  }, [handleViewChange]);

  // --- Loading and Error States ---
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Market Analysis...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading market analysis: {error}</Alert>;
  }

  if (!marketReportFull?.market_report) {
    return <Alert severity="warning">Market analysis data is currently unavailable.</Alert>;
  }

  // --- Render Content Based on View ---
  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <MarketDashboard
            marketReport={marketReportFull}
            trendReports={marketReportFull.trend_reports}
            renderMarketRating={renderMarketRating}
            renderMarketTrend={renderMarketTrend}
            renderMarketRegime={renderMarketRegime}
            onTabChange={handleTabChange}
            onTickerSelect={onTickerSelect}
          />
        );
        
      case 'insights':
        return (
          <MarketInsightsReport
            marketReportFull={marketReportFull}
            renderMarketRating={renderMarketRating}
            renderMarketRegime={renderMarketRegime}
            onTabChange={handleTabChange}
          />
        );
        
      case 'trends':
      case 'performance':
      case 'risk-level':
      case 'money-flow':
        return (
          <MarketFactorsView
            marketReport={marketReportFull}
            renderMarketRating={renderMarketRating}
            renderMarketTrend={renderMarketTrend}
            onTabChange={handleTabChange}
            initialTab={(() => {
              // Map views to tabs
              if (currentView === 'performance') return 0;
              if (currentView === 'risk-level') return 1;
              if (currentView === 'money-flow') return 2;
              
              // Otherwise use the trendTab parameter
              const trendTabParam = new URLSearchParams(location.search).get('trendTab');
              if (trendTabParam !== null) {
                const trendTab = parseInt(trendTabParam, 10);
                if (!isNaN(trendTab) && trendTab >= 0 && trendTab <= 2) {
                  return trendTab;
                }
              }
              return 0; // Default to first tab
            })()}
            onTickerSelect={onTickerSelect}
            view={currentView} // Pass the current view
          />
        );
        
      case 'sectors':
        return (
          <MarketSectorsView
            marketReport={marketReportFull}
            renderMarketRating={renderMarketRating}
            renderMarketTrend={renderMarketTrend}
            onTabChange={handleTabChange}
            onTickerSelect={onTickerSelect}
          />
        );
        
      case 'market-caps':
        return (
          <MarketCapsView
            marketReport={marketReportFull}
            renderMarketRating={renderMarketRating}
            renderMarketTrend={renderMarketTrend}
            onTabChange={handleTabChange}
            onTickerSelect={onTickerSelect}
          />
        );
        
      case 'recommendations':
        return (
          <MarketRecommendations
            reportData={marketReportFull}
            renderMarketRating={renderMarketRating}
            renderMarketTrend={renderMarketTrend}
            initialTab="invest"
            onTickerSelect={onTickerSelect}
          />
        );
        
      case 'allocation':
        return (
          <MarketAllocationView
            marketReport={marketReportFull}
            renderMarketRating={renderMarketRating}
            renderMarketTrend={renderMarketTrend}
            onTabChange={handleTabChange}
            onTickerSelect={onTickerSelect}
          />
        );
        
      case 'questions':
        return (
          <MarketQuestionsView
            marketReport={marketReportFull}
            renderMarketRating={renderMarketRating}
            renderMarketTrend={renderMarketTrend}
            onTabChange={handleTabChange}
            onTickerSelect={onTickerSelect}
          />
        );

      default:
        // Redirect to dashboard if view is unknown
        handleViewChange('dashboard');
        return null;
    }
  };

  return (
    <Box>
      {renderContent()}
    </Box>
  );
};

export default MarketAnalysisWorkflow;